import { queryKeys } from '@/lib/query-keys';
import type {
  AnalyticsLine,
  AnalyticsUsageResponse,
  UnusedLine,
} from '@octave/contracts';
import {
  AnalyticsFilters,
  UnusedLinesFilters,
} from '@/types/mobile-line.dto';
import {
  useQuery,
  } from '@tanstack/react-query';
import {
  analyticsClient,
  } from '@/lib/api/ts-rest';


export const useMobileLineUsage = (
  lineId: string | undefined,
  filters: AnalyticsFilters,
) => {
  return useQuery<AnalyticsUsageResponse, Error>({
    queryKey: queryKeys.lines.usage(lineId, filters),
    queryFn: async () => {
      if (!lineId) throw new Error('lineId is required');
      const res = await analyticsClient.getLineUsage({
        params: { lineId },
        query: {
          productType: filters.productType,
          startMonth: filters.startMonth,
          startYear: filters.startYear,
          startDay: filters.startDay,
          endMonth: filters.endMonth,
          endYear: filters.endYear,
          endDay: filters.endDay,
        },
      });
      if (res.status !== 200) throw new Error('Failed to load usage');
      return res.body;
    },
    enabled: !!lineId,
  });
};

export const useMobileLinesLight = (
  organizationId: string | undefined,
  siteId: string | undefined,
  filters?: { operators?: string[]; types?: string[] },
) => {
  return useQuery<AnalyticsLine[], Error>({
    queryKey: queryKeys.lines.light(organizationId, siteId, filters),
    queryFn: async () => {
      if (!organizationId) throw new Error('No organization selected');
      const query = { operator: filters?.operators, type: filters?.types };
      const res = siteId
        ? await analyticsClient.listSiteAnalyticsLines({
            params: { organizationId, siteId },
            query,
          })
        : await analyticsClient.listAnalyticsLines({
            params: { organizationId },
            query,
          });
      if (res.status !== 200) throw new Error('Failed to load lines');
      return res.body;
    },
    enabled: !!organizationId,
  });
};

export const useUnusedLines = (
  organizationId: string | undefined,
  siteId: string | undefined,
  filters: UnusedLinesFilters | null,
) => {
  return useQuery<UnusedLine[], Error>({
    queryKey: queryKeys.lines.unused(organizationId, siteId, filters),
    queryFn: async () => {
      if (!filters) throw new Error('Filters are required');
      if (!organizationId) throw new Error('No organization selected');
      const res = siteId
        ? await analyticsClient.listSiteUnusedLines({
            params: { organizationId, siteId },
            query: filters,
          })
        : await analyticsClient.listUnusedLines({
            params: { organizationId },
            query: filters,
          });
      if (res.status !== 200) throw new Error('Failed to load unused lines');
      return res.body;
    },
    enabled: !!organizationId && !!filters,
  });
};
